import { useCallback, useMemo, useState } from 'react';
import { Download, Loader2, Lock } from 'lucide-react';
import {
  fillIrsFromSecureVault,
  getSecureFormStatus,
  submitSecureForm,
  type SecureFormField,
} from '../../lib/api';
import {
  SecureFormCard,
  SecureFormFieldControl,
  SecureFormFieldLabel,
  SecureFormShareStrip,
  formatSsnInput,
  maskSsnDisplay,
} from './secureFormUi';

const ALLOWED_FIELD_TYPES = new Set([
  'text',
  'email',
  'tel',
  'date',
  'ssn',
  'ein',
  'address',
  'select',
]);
const FIELD_KEY_RE = /^[a-z][a-z0-9_]{1,63}$/;
const MAX_FIELDS = 20;
const MAX_LABEL_LEN = 120;

interface SecureFormBubbleProps {
  formId: string;
  title: string;
  fields: SecureFormField[];
  irsFormNumber?: string;
  fieldMapping?: Record<string, string>;
  shareUrl?: string;
  recipientName?: string;
  expiresAt?: string;
  purpose?: string;
  toolCallId?: string;
  onSaved?: (filledKeys: string[]) => void;
  onFilled?: (downloadUrl: string) => void;
}

function stripMarkdown(text: string): string {
  return text
    .replace(/[*_`#[\]|]/g, '')
    .replace(/https?:\/\/\S+/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Defensive: skip invalid fields so a sloppy schema never crashes the bubble. */
export function sanitizeSecureFormFields(fields: SecureFormField[] | undefined | null): SecureFormField[] {
  if (!Array.isArray(fields)) return [];
  const seen = new Set<string>();
  const out: SecureFormField[] = [];
  for (const raw of fields) {
    if (!raw || typeof raw !== 'object') continue;
    const key = typeof raw.key === 'string' ? raw.key.trim().toLowerCase() : '';
    if (!FIELD_KEY_RE.test(key) || seen.has(key)) continue;
    const label = stripMarkdown(typeof raw.label === 'string' ? raw.label : '').slice(0, MAX_LABEL_LEN);
    if (!label) continue;
    const type = ALLOWED_FIELD_TYPES.has(raw.type) ? raw.type : 'text';
    seen.add(key);
    const field: SecureFormField = {
      key,
      label,
      type,
      required: raw.required !== false,
      sensitive: raw.sensitive !== false,
    };
    if (type === 'select' && Array.isArray((raw as SecureFormField & { options?: string[] }).options)) {
      const options = ((raw as SecureFormField & { options?: string[] }).options ?? [])
        .filter((o): o is string => typeof o === 'string')
        .map((o) => stripMarkdown(o).slice(0, 80))
        .filter(Boolean)
        .slice(0, 30);
      if (options.length > 0) {
        (field as SecureFormField & { options?: string[] }).options = options;
      } else {
        field.type = 'text';
      }
    }
    out.push(field);
    if (out.length >= MAX_FIELDS) break;
  }
  return out;
}

function sanitizeTitle(title: string | undefined | null): string {
  const cleaned = stripMarkdown(title ?? '');
  return (cleaned || 'Secure information').slice(0, MAX_LABEL_LEN);
}

function SecureFormBubble({
  formId,
  title,
  fields,
  irsFormNumber,
  fieldMapping,
  shareUrl,
  recipientName,
  expiresAt,
  purpose,
  onSaved,
  onFilled,
}: SecureFormBubbleProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [focused, setFocused] = useState<Record<string, boolean>>({});
  const [filledKeys, setFilledKeys] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [filling, setFilling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const safeTitle = useMemo(() => sanitizeTitle(title), [title]);
  const safeFields = useMemo(() => sanitizeSecureFormFields(fields), [fields]);
  const safePurpose = purpose ? stripMarkdown(purpose).slice(0, 80) : undefined;

  const refreshStatus = useCallback(async () => {
    const res = await getSecureFormStatus(formId);
    if (res.ok && res.data?.fields) {
      const filled = new Set(
        Object.entries(res.data.fields)
          .filter(([, v]) => v)
          .map(([k]) => k)
      );
      setFilledKeys(filled);
    }
  }, [formId]);

  const handleChange = (key: string, raw: string, type: SecureFormField['type']) => {
    const next = type === 'ssn' ? formatSsnInput(raw) : raw;
    setValues((prev) => ({ ...prev, [key]: next }));
  };

  const displayValue = (field: SecureFormField) => {
    const val = values[field.key] ?? '';
    if (field.type === 'ssn' && val && !focused[field.key]) {
      return maskSsnDisplay(val);
    }
    return val;
  };

  const canSave = useMemo(() => {
    if (safeFields.length === 0) return false;
    const required = safeFields.filter((f) => f.required);
    return required.every((f) => (values[f.key] ?? '').trim().length > 0);
  }, [safeFields, values]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, string> = {};
      for (const f of safeFields) {
        const v = (values[f.key] ?? '').trim();
        if (v) payload[f.key] = values[f.key] ?? '';
      }
      const res = await submitSecureForm(formId, payload);
      if (!res.ok || !res.data) {
        const msg = res.error?.message ?? 'Save failed';
        const status = res.error?.status ?? res.status;
        if (status === 410 || /expired/i.test(msg)) {
          setError('This secure form has expired. Ask the assistant to create a new one.');
        } else if (status === 404 || /not found/i.test(msg)) {
          setError('Secure form not found. Refresh chat or ask the assistant to recreate it.');
        } else if (status === 502 || status === 503 || status === 504 || status === 0) {
          setError(
            /still unavailable/i.test(msg)
              ? msg
              : 'Server temporarily unavailable after multiple attempts. Please try again in a moment.'
          );
        } else {
          setError(msg);
        }
        return;
      }
      setSaved(true);
      setFilledKeys(new Set(res.data.filled_keys));
      onSaved?.(res.data.filled_keys);
      await refreshStatus();
    } finally {
      setSaving(false);
    }
  };

  const handleFill = async () => {
    if (!irsFormNumber) return;
    setFilling(true);
    setError(null);
    const mapping: Record<string, string> = fieldMapping ?? {};
    if (Object.keys(mapping).length === 0) {
      for (const field of safeFields) {
        mapping[field.key] = field.key;
      }
    }
    try {
      const res = await fillIrsFromSecureVault(formId, {
        form_number: irsFormNumber,
        field_mapping: mapping,
      });
      if (!res.ok || !res.data?.download_url) {
        setError(res.error?.message ?? res.data?.error ?? 'Fill failed');
        return;
      }
      setDownloadUrl(res.data.download_url);
      onFilled?.(res.data.download_url);
    } finally {
      setFilling(false);
    }
  };

  return (
    <SecureFormCard title={safeTitle} className="my-3">
      {shareUrl && (
        <SecureFormShareStrip
          shareUrl={shareUrl}
          recipientName={recipientName}
          expiresAt={expiresAt}
          purpose={safePurpose}
        />
      )}

      <p className="text-xs leading-relaxed text-zt-muted">
        {shareUrl
          ? 'Copy the link for the recipient, or enter values here. Values are encrypted; the assistant only sees field names and fill status.'
          : 'Enter sensitive information below. Values are encrypted and stored securely; the assistant only sees field names and fill status.'}
      </p>

      {safeFields.length === 0 && (
        <p className="text-xs text-zt-warning">
          No valid fields on this form. Ask the assistant to recreate it with a strict schema.
        </p>
      )}

      {safeFields.map((field) => (
        <label key={field.key} className="block min-w-0">
          <SecureFormFieldLabel
            label={field.label}
            required={field.required}
            saved={filledKeys.has(field.key)}
          />
          <SecureFormFieldControl
            field={field}
            value={displayValue(field)}
            onChange={(raw) => handleChange(field.key, raw, field.type)}
            onFocus={() => setFocused((p) => ({ ...p, [field.key]: true }))}
            onBlur={() => setFocused((p) => ({ ...p, [field.key]: false }))}
          />
        </label>
      ))}

      {error && <p className="break-words text-xs text-zt-danger">{error}</p>}

      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          disabled={!canSave || saving}
          onClick={() => void handleSave()}
          className="flex items-center gap-1.5 rounded-lg bg-zt-primary px-3.5 py-2 text-xs font-medium text-zt-bg hover:bg-zt-accent disabled:opacity-40"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />}
          Save securely
        </button>
        {saved && irsFormNumber && (
          <button
            type="button"
            disabled={filling}
            onClick={() => void handleFill()}
            className="flex items-center gap-1.5 rounded-lg border border-zt-primary/40 px-3.5 py-2 text-xs font-medium text-emerald-100 hover:bg-zt-primary/10 disabled:opacity-40"
          >
            {filling ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Fill form
          </button>
        )}
        {downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 rounded-lg border border-zt-border px-3.5 py-2 text-xs text-slate-200 hover:border-zt-primary"
          >
            <Download size={13} /> Download PDF
          </a>
        )}
      </div>
    </SecureFormCard>
  );
}

export default SecureFormBubble;
